import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useUser } from '../../shared/context/UserContext';
import { useReferenceData } from '../../shared/hooks/useReferenceData';
import { getSites } from '../inventory/api/inventoryApi';
import { getDispositionReport, generateDispositionXlsx, generateSiteStatusPdf } from './api/dispositionApi';
import DispositionFilterBar from './components/DispositionFilterBar';
import DispositionStatsStrip from './components/DispositionStatsStrip';
import DispositionToolList from './components/DispositionToolList';
import DispositionDetailPane from './components/DispositionDetailPane';
import SiteStatusPdfPickerModal from './components/SiteStatusPdfPickerModal';
import './DispositionReportingPage.css';

/* ── Constants ─────────────────────────────────────────────────────── */
const DEFAULT_SITE_ID = 'site-c8d7d728-c786-4cef-a97b-d44967019b43';

/* ── Splitter persistence (mirror Disposal Recon pattern) ──────────── */
const SPLIT_MIN     = 0.25;
const SPLIT_MAX     = 0.50;
const SPLIT_DEFAULT = 0.35;

function getSplitKey(email) {
  return `tmg_disposition_split_ratio_${email || 'default'}`;
}

function loadSplitRatio(email) {
  try {
    const v = parseFloat(localStorage.getItem(getSplitKey(email)));
    if (!isNaN(v) && v >= SPLIT_MIN && v <= SPLIT_MAX) return v;
  } catch { /* ignore */ }
  return SPLIT_DEFAULT;
}

function saveSplitRatio(email, ratio) {
  try { localStorage.setItem(getSplitKey(email), String(ratio)); } catch { /* ignore */ }
}

/* ── Generate-CTA helpers ───────────────────────────────────────────── */
const TOAST_AUTO_DISMISS_MS = 5000;
const FALLBACK_DOWNLOAD_FILENAME = 'disposition_package.xlsx';

function parseFilenameFromContentDisposition(header) {
  if (!header) return null;
  // RFC 6266 'attachment; filename="..."' -- backend always quotes the name.
  const match = /filename="([^"]+)"/.exec(header);
  return match ? match[1] : null;
}


export default function DispositionReportingPage() {
  const navigate = useNavigate();
  const { callerEmail } = useUser();
  const location = useLocation();

  /* ── Engine 2 handoff (read ONCE from router state, NOT query params) ───
   * When the warehouse "Start disposition" hands off a tool set, run in
   * HANDOFF MODE: site_id is NOT pinned (cross-site home≠storage tools must
   * not be filtered by storage site — the engine scopes via tool_ids ∩
   * allowed_sites). tool_ids + dwell_override_tool_ids ride the request
   * bodies; the OEM filter still applies and the handoff set persists across
   * OEM changes (that's the per-OEM split). */
  const [handoffToolIds] = useState(() => location.state?.tool_ids || null);
  const [handoffOverrideIds] = useState(() => location.state?.dwell_override_tool_ids || null);
  const isHandoff = Array.isArray(handoffToolIds) && handoffToolIds.length > 0;

  /* ── URL-mirrored filter state ─────────────────────────────────── */
  const [searchParams, setSearchParams] = useSearchParams();
  const siteId   = searchParams.get('site_id')   || DEFAULT_SITE_ID;
  const oem      = searchParams.get('oem')       || '';
  const gapsOnly = searchParams.get('gaps_only') === '1';

  const updateFilter = useCallback((patch) => {
    const next = new URLSearchParams(searchParams);
    if ('site_id' in patch) {
      if (patch.site_id) next.set('site_id', patch.site_id);
      else next.delete('site_id');
    }
    if ('oem' in patch) {
      if (patch.oem) next.set('oem', patch.oem);
      else next.delete('oem');
    }
    if ('gaps_only' in patch) {
      if (patch.gaps_only) next.set('gaps_only', '1');
      else next.delete('gaps_only');
    }
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  /* ── Sites (for site selector) ─────────────────────────────────── */
  const [sites, setSites]               = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);

  useEffect(() => {
    if (!callerEmail) return;
    let cancelled = false;
    setSitesLoading(true);
    getSites(callerEmail)
      .then(data => { if (!cancelled) { setSites(Array.isArray(data) ? data : []); setSitesLoading(false); } })
      .catch(err => {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.warn('getSites failed:', err);
          setSites([]);
          setSitesLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [callerEmail]);

  /* ── OEM dropdown options (ReferenceData) ──────────────────────── */
  const { options: refOptions, loading: refLoading } = useReferenceData(
    ['oem'], callerEmail
  );
  const oemOptions = useMemo(() => refOptions?.oem || [], [refOptions]);

  /* ── Disposition report (the actual data) ──────────────────────── */
  const [tools, setTools]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);

  useEffect(() => {
    if (!callerEmail) return;
    if (!isHandoff && !siteId && !oem) { setTools([]); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getDispositionReport({
      siteId: isHandoff ? null : siteId,   // handoff: don't pin storage site
      oem: oem || null,
      callerEmail,
      toolIds: isHandoff ? handoffToolIds : undefined,
      dwellOverrideToolIds: isHandoff ? handoffOverrideIds : undefined,
    })
      .then(data => {
        if (cancelled) return;
        setTools(data.tools || []);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.message || 'Failed to load disposition report.');
        setTools([]);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [callerEmail, siteId, oem, isHandoff, handoffToolIds, handoffOverrideIds]);

  /* ── Selected tool (local; reset whenever the tools array changes) ── */
  const [selectedToolId, setSelectedToolId] = useState(null);
  useEffect(() => { setSelectedToolId(null); }, [tools]);

  const selectedTool = useMemo(
    () => tools.find(t => t.tool_id === selectedToolId) || null,
    [tools, selectedToolId]
  );

  /* ── Smart back: prefer browser back to preserve filter URL state;
   * fall back to /admin only when arriving via deep link / bookmark
   * (no prior history to traverse). Avoids the L-Assignment-default
   * surprise when the user came in from the admin flyout. */
  const handleBack = useCallback(() => {
    const hasHistory = (window.history.state?.idx ?? 0) > 0;
    if (hasHistory) {
      navigate(-1);
    } else {
      navigate('/admin');
    }
  }, [navigate]);

  /* ── Generate Disposition Package CTA state ──────────────────────── */
  const [isGenerating, setIsGenerating] = useState(false);
  const [toast, setToast] = useState(null); // { message, variant: 'error' }

  // Auto-dismiss toast after TOAST_AUTO_DISMISS_MS.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), TOAST_AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [toast]);

  // Disabled when no OEM selected, when ADIENT (self-scrap, no external file),
  // or while a generation request is in flight.
  const ctaDisabled = isGenerating || !oem || oem === 'ADIENT';
  const ctaTooltip = !oem
    ? 'Select an OEM to generate a disposition package.'
    : oem === 'ADIENT'
    ? "Self-scrap doesn't require an external submission."
    : '';

  const handleGenerate = useCallback(async () => {
    if (ctaDisabled) return;
    setIsGenerating(true);
    try {
      const res = await generateDispositionXlsx({
        siteId: isHandoff ? null : siteId,
        oem,
        callerEmail,
        toolIds: isHandoff ? handoffToolIds : undefined,
        dwellOverrideToolIds: isHandoff ? handoffOverrideIds : undefined,
      });
      const ct = res.headers.get('content-type') || '';

      if (ct.includes('application/json')) {
        // Empty-submittable, OEM-not-supported, or other backend envelope.
        const data = await res.json().catch(() => ({}));
        const message = data.message || data.error || 'Generation failed.';
        setToast({ message, variant: 'error' });
        return;
      }

      // Binary .xlsx -> blob download.
      const blob = await res.blob();
      const filename =
        parseFilenameFromContentDisposition(res.headers.get('content-disposition'))
        || FALLBACK_DOWNLOAD_FILENAME;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setToast({
        message: `Generation failed: ${err.message || 'network error'}.`,
        variant: 'error',
      });
    } finally {
      setIsGenerating(false);
    }
  }, [ctaDisabled, siteId, oem, callerEmail, isHandoff, handoffToolIds, handoffOverrideIds]);

  /* ── Generate Site Status PDF CTA state (Phase 5b-ii + Phase 2 picker) ─
   * Independent from the Disposition Package CTA. Active for ALL OEM
   * filters including ADIENT and "All OEMs". Empty-state filter combos
   * (e.g., site=Adient + oem=Toyota) flow through the blob-download
   * branch -- the backend returns a 1-page empty-state PDF, not a JSON
   * envelope, so the user gets a download rather than a toast.
   *
   * Phase 2 (May 5): the Generate button now opens the Field Picker
   * modal instead of firing the fetch directly. The picker's onConfirm
   * callback (handlePickerConfirm) wraps the existing fetch logic with
   * the picker's selectedKeys + includePhotos as additional body params.
   * Picker selection is lifted here for tab-session persistence (L28);
   * null sentinels trigger default-init from the manifest on first open. */
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [showFieldPickerModal, setShowFieldPickerModal] = useState(false);
  const [pickerSelectedKeys, setPickerSelectedKeys] = useState(null);
  const [pickerIncludePhotos, setPickerIncludePhotos] = useState(null);
  const ctaPdfDisabled = isGeneratingPdf || !siteId;
  const ctaPdfTooltip = !siteId
    ? 'Select a site to generate a Site Status PDF.'
    : '';

  const handleGeneratePdf = useCallback(() => {
    if (ctaPdfDisabled) return;
    setShowFieldPickerModal(true);
  }, [ctaPdfDisabled]);

  const handlePickerSelectionChange = useCallback((selectedKeys, includePhotos) => {
    setPickerSelectedKeys(selectedKeys);
    setPickerIncludePhotos(includePhotos);
  }, []);

  const handlePickerClose = useCallback(() => {
    setShowFieldPickerModal(false);
  }, []);

  const handlePickerConfirm = useCallback(async (selectedKeys, includePhotos) => {
    setPickerSelectedKeys(selectedKeys);
    setPickerIncludePhotos(includePhotos);
    setShowFieldPickerModal(false);
    setIsGeneratingPdf(true);
    try {
      const res = await generateSiteStatusPdf({
        siteId,
        oem,
        gapsOnly,
        callerEmail,
        fields: selectedKeys,
        includePhotos,
      });
      const ct = res.headers.get('content-type') || '';

      if (ct.includes('application/json')) {
        // SITE_NOT_FOUND, GENERATION_FAILED, or one of the four picker
        // validation envelopes (unknown_fields, missing_mandatory_field,
        // too_many_fields, width_overflow). All ride the same toast channel.
        const data = await res.json().catch(() => ({}));
        const message = data.message || data.error || 'PDF generation failed.';
        setToast({ message, variant: 'error' });
        return;
      }

      // Binary .pdf -> blob download.
      const blob = await res.blob();
      const filename =
        parseFilenameFromContentDisposition(res.headers.get('content-disposition'))
        || 'site_status.pdf';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setToast({
        message: `PDF generation failed: ${err.message || 'network error'}.`,
        variant: 'error',
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [siteId, oem, gapsOnly, callerEmail]);

  /* ── Resizable splitter ────────────────────────────────────────── */
  const [splitRatio, setSplitRatio] = useState(() => loadSplitRatio(callerEmail));
  const draggingRef = useRef(false);
  const pageRef     = useRef(null);

  useEffect(() => {
    setSplitRatio(loadSplitRatio(callerEmail));
  }, [callerEmail]);

  const handleSplitterMouseDown = useCallback((e) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    function onMouseMove(ev) {
      if (!draggingRef.current || !pageRef.current) return;
      const rect = pageRef.current.getBoundingClientRect();
      const ratio = (ev.clientX - rect.left) / rect.width;
      const clamped = Math.min(SPLIT_MAX, Math.max(SPLIT_MIN, ratio));
      setSplitRatio(clamped);
    }

    function onMouseUp() {
      if (draggingRef.current) {
        draggingRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        setSplitRatio(prev => { saveSplitRatio(callerEmail, prev); return prev; });
      }
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    }

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [callerEmail]);

  const leftPct = `${(splitRatio * 100).toFixed(1)}%`;

  /* ── Render ────────────────────────────────────────────────────── */
  return (
    <div className="dispositions-page" ref={pageRef}>
      {/* ── Header (back + title + CTA) ──────────────────────────── */}
      <header className="dispositions-header">
        <button
          className="dispositions-back"
          onClick={handleBack}
          aria-label="Back"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="dispositions-header-text">
          <h1 className="dispositions-header-title">Disposition Reporting</h1>
        </div>
        <span className="dispositions-cta-wrapper" title={ctaTooltip}>
          <button
            type="button"
            className="dispositions-cta"
            onClick={handleGenerate}
            disabled={ctaDisabled}
          >
            {isGenerating ? 'Generating...' : 'Generate Disposition Package'}
          </button>
        </span>
        <span className="dispositions-cta-wrapper" title={ctaPdfTooltip}>
          <button
            type="button"
            className="dispositions-cta"
            onClick={handleGeneratePdf}
            disabled={ctaPdfDisabled}
          >
            {isGeneratingPdf ? 'Generating...' : 'Generate Site Status PDF'}
          </button>
        </span>
      </header>

      {toast && (
        <div
          className={`dispositions-toast dispositions-toast-${toast.variant}`}
          role="status"
          aria-live="polite"
        >
          {toast.message}
        </div>
      )}

      {/* ── Body: two-pane ───────────────────────────────────────── */}
      <div className="dispositions-body-split">
        <div className="dispositions-left" style={{ width: leftPct }}>
          <DispositionFilterBar
            sites={sites}
            sitesLoading={sitesLoading}
            siteId={siteId}
            onSiteChange={(v) => updateFilter({ site_id: v })}
            oem={oem}
            onOemChange={(v) => updateFilter({ oem: v })}
            oemOptions={oemOptions}
            oemLoading={refLoading}
            gapsOnly={gapsOnly}
            onGapsOnlyChange={(v) => updateFilter({ gaps_only: v })}
          />

          <DispositionStatsStrip tools={tools} />

          <div className="dispositions-list-area">
            <DispositionToolList
              tools={tools}
              selectedToolId={selectedToolId}
              onSelect={setSelectedToolId}
              gapsOnly={gapsOnly}
              loading={loading}
              error={error}
            />
          </div>
        </div>

        <div
          className="dispositions-splitter"
          onMouseDown={handleSplitterMouseDown}
          role="separator"
          aria-orientation="vertical"
        />

        <div className="dispositions-right">
          <DispositionDetailPane tool={selectedTool} />
        </div>
      </div>

      <SiteStatusPdfPickerModal
        isOpen={showFieldPickerModal}
        onClose={handlePickerClose}
        onConfirm={handlePickerConfirm}
        selectedKeys={pickerSelectedKeys}
        includePhotos={pickerIncludePhotos}
        onSelectionChange={handlePickerSelectionChange}
      />
    </div>
  );
}
